import React, { useEffect, useState, useMemo } from 'react';
import { GlobalState, globalState } from '../../models';
import { LOCALE_KEY } from '@fx-ui/intl';
import { ConfigProvider as BizConfigProvider } from '@fx-ui/jdy-design-biz';
import zh_cn from '../../locales/zh_cn.json';
import { initGlobalVariables } from '../../utils';
import { platformIntl } from '../../constants';
import { ConfigProviderContext } from '../../contexts';

import BIZ_ZH_CN from '@fx-ui/jdy-design-biz/locale/zh_CN';
import BIZ_ZH_TW from '@fx-ui/jdy-design-biz/locale/zh_TW';
import BIZ_EN_US from '@fx-ui/jdy-design-biz/locale/en_US';
import BIZ_JA_JP from '@fx-ui/jdy-design-biz/locale/ja_JP';
import BIZ_VI_VN from '@fx-ui/jdy-design-biz/locale/vi_VN';
import BIZ_RU_RU from '@fx-ui/jdy-design-biz/locale/ru_RU';
import BIZ_FR_FR from '@fx-ui/jdy-design-biz/locale/fr_FR';
import BIZ_ES_ES from '@fx-ui/jdy-design-biz/locale/es_ES';
import BIZ_TH_TH from '@fx-ui/jdy-design-biz/locale/th_TH';
import BIZ_ID_ID from '@fx-ui/jdy-design-biz/locale/id_ID';
import BIZ_KO_KR from '@fx-ui/jdy-design-biz/locale/ko_KR';
import BIZ_DE_DE from '@fx-ui/jdy-design-biz/locale/de_DE';
import BIZ_PT_PT from '@fx-ui/jdy-design-biz/locale/pt_PT';
import BIZ_KM_KH from '@fx-ui/jdy-design-biz/locale/km_KH';
import BIZ_LO_LA from '@fx-ui/jdy-design-biz/locale/lo_LA';
import BIZ_HI_IN from '@fx-ui/jdy-design-biz/locale/hi_IN';
import BIZ_BN_BD from '@fx-ui/jdy-design-biz/locale/bn_BD';
import BIZ_TA_IN from '@fx-ui/jdy-design-biz/locale/ta_IN';
import BIZ_UZ_UZ from '@fx-ui/jdy-design-biz/locale/uz_UZ';
import BIZ_MN_MN from '@fx-ui/jdy-design-biz/locale/mn_MN';
import BIZ_IT_IT from '@fx-ui/jdy-design-biz/locale/it_IT';
import BIZ_NL_NL from '@fx-ui/jdy-design-biz/locale/nl_NL';
import BIZ_PL_PL from '@fx-ui/jdy-design-biz/locale/pl_PL';
import BIZ_HU_HU from '@fx-ui/jdy-design-biz/locale/hu_HU';
import BIZ_PT_BR from '@fx-ui/jdy-design-biz/locale/pt_BR';

const JDY_DESIGN_BIZ_LOCALE: Record<LOCALE_KEY, typeof BIZ_ZH_CN> = {
    [LOCALE_KEY.ZH_CN]: BIZ_ZH_CN,
    [LOCALE_KEY.ZH_TW]: BIZ_ZH_TW,
    [LOCALE_KEY.EN_US]: BIZ_EN_US,
    [LOCALE_KEY.JA_JP]: BIZ_JA_JP,
    [LOCALE_KEY.VI_VN]: BIZ_VI_VN,
    [LOCALE_KEY.RU_RU]: BIZ_RU_RU,
    [LOCALE_KEY.FR_FR]: BIZ_FR_FR,
    [LOCALE_KEY.ES_ES]: BIZ_ES_ES,
    [LOCALE_KEY.TH_TH]: BIZ_TH_TH,
    [LOCALE_KEY.ID_ID]: BIZ_ID_ID,
    [LOCALE_KEY.KO_KR]: BIZ_KO_KR,
    [LOCALE_KEY.DE_DE]: BIZ_DE_DE,
    [LOCALE_KEY.PT_PT]: BIZ_PT_PT,
    [LOCALE_KEY.KM_KH]: BIZ_KM_KH,
    [LOCALE_KEY.LO_LA]: BIZ_LO_LA,
    [LOCALE_KEY.HI_IN]: BIZ_HI_IN,
    [LOCALE_KEY.BN_BD]: BIZ_BN_BD,
    [LOCALE_KEY.TA_IN]: BIZ_TA_IN,
    [LOCALE_KEY.UZ_UZ]: BIZ_UZ_UZ,
    [LOCALE_KEY.MN_MN]: BIZ_MN_MN,
    [LOCALE_KEY.IT_IT]: BIZ_IT_IT,
    [LOCALE_KEY.NL_NL]: BIZ_NL_NL,
    [LOCALE_KEY.PL_PL]: BIZ_PL_PL,
    [LOCALE_KEY.HU_HU]: BIZ_HU_HU,
    [LOCALE_KEY.PT_BR]: BIZ_PT_BR
};

export interface ConfigProviderProps
    extends Pick<
        GlobalState,
        | 'suiteId'
        | 'csrfToken'
        | 'reqHeaders'
        | 'tracker'
        | 'origin'
        | 'platformSite'
        | 'externalUserBeta'
        | 'localeKey'
        | 'defaultLocaleKey'
        | 'onRefreshToken'
        | 'userTenantsFilter'
        | 'onSwitchCorp'
    > {
    locale: typeof zh_cn;
    accessToken?: string;
    /**
     * h5JsApi 是否就绪
     */
    h5JsApiReady?: boolean;
    children: React.ReactElement;
}

/**
 * 全局变量初始化，不依赖登录状态
 */
const ConfigProvider: React.FC<ConfigProviderProps> = (props: ConfigProviderProps) => {
    const {
        suiteId,
        localeKey = LOCALE_KEY.ZH_CN,
        defaultLocaleKey = localeKey,
        locale,
        origin,
        platformSite,
        externalUserBeta = false,
        csrfToken,
        reqHeaders,
        accessToken,
        onRefreshToken,
        tracker,
        userTenantsFilter,
        onSwitchCorp,
        h5JsApiReady,
        children
    } = props;

    const [loading, setLoading] = useState(true);
    const contextValue = useMemo(() => ({ h5JsApiReady }), [h5JsApiReady]);

    useEffect(() => {
        globalState.suiteId = suiteId;
        globalState.origin = origin;
        globalState.platformSite = platformSite;
        globalState.externalUserBeta = externalUserBeta;
        globalState.csrfToken = csrfToken;
        globalState.reqHeaders = reqHeaders;
        globalState.localeKey = localeKey;
        globalState.defaultLocaleKey = defaultLocaleKey;
        // SDK 初始化时传入了 accessToken，则认为是跨域请求
        globalState.isCors = !!accessToken;
        globalState.setAccessToken(accessToken);
        globalState.tracker = tracker;
        globalState.onRefreshToken = onRefreshToken;
        globalState.userTenantsFilter = userTenantsFilter;
        globalState.onSwitchCorp = onSwitchCorp;

        platformIntl.init(localeKey, locale);

        initGlobalVariables().then(() => {
            setLoading(false);
        });
    }, []);

    return loading ? null : (
        <BizConfigProvider locale={JDY_DESIGN_BIZ_LOCALE[localeKey]}>
            <ConfigProviderContext.Provider value={contextValue}>
                {children}
            </ConfigProviderContext.Provider>
        </BizConfigProvider>
    );
};

export default ConfigProvider;
